"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaNode = exports.FormulaNodeData = void 0;
const tree_node_1 = require("./base/tree.node");
const internal_node_1 = require("./base/internal.node");
const concrete_node_1 = require("./concrete.node");
const sheet_db_1 = require("../../sheet_db");
class FormulaNodeData {
    name;
    description;
    returnType;
    isWindowFunction;
    args;
}
exports.FormulaNodeData = FormulaNodeData;
class FormulaNode extends internal_node_1.InternalNode {
    _abstractText; // formula canonical name (en)
    _formulaNameToken; // original token from parser
    _formulaName; // same as token, but uppercase
    _translatedFormulaName; // target formula translation (depends on locale and isOut)
    _formulaData;
    _formulaDef;
    _formulaTranslations;
    constructor(originalNode, astParent) {
        super(originalNode, astParent);
        this._formulaNameToken = originalNode.ID().getText();
        this._formulaName = this._formulaNameToken.toUpperCase();
        this._abstractText = this._formulaName;
        this._argsStartPosition = (originalNode.LPAREN()?.symbol.start ?? originalNode.start.start) + 1;
        this._argsEndPosition = originalNode.RPAREN()?.symbol.stop ?? originalNode.start.stop;
        // when formula has no arguments start and end position are the same
        if (originalNode.children?.length === 3) {
            this._argsStartPosition = originalNode.stop?.stop;
            this._argsEndPosition = originalNode.stop?.stop;
        }
        this._tokenStartPosition = originalNode.ID().symbol.start;
        this._tokenEndPosition = originalNode.ID().symbol.stop;
        this._startPosition = this._tokenStartPosition ?? 0;
        this._endPosition = this._argsEndPosition ?? 1;
        this._treeNodeType = tree_node_1.TreeNodeType.FORMULA_NODE;
    }
    accept(v) {
        if (v.visitFormula)
            v.visitFormula(this);
        this.visitChildren(v);
    }
    get formulaNameToken() {
        return this._formulaNameToken;
    }
    get formulaDef() {
        return this._formulaDef;
    }
    get formulaTranslations() {
        return this._formulaTranslations;
    }
    get formulaData() {
        return this._formulaData;
    }
    set formulaData(value) {
        this._formulaData = value;
    }
    get abstractText() {
        return this._abstractText;
    }
    get argsStartPosition() {
        return this._argsStartPosition ?? 0;
    }
    get argsEndPosition() {
        return this._argsEndPosition ?? 0;
    }
    get formulaName() {
        return this._formulaName;
    }
    set formulaName(value) {
        this._formulaName = value;
    }
    get translatedFormulaName() {
        return this._translatedFormulaName;
    }
    // this overrides get children to clean remaining concrete nodes from our AST (to be used on semantic validations)
    get children() {
        return this._children.filter(child => !(child instanceof concrete_node_1.ConcreteNode));
    }
    get rawChildren() {
        return this._children;
    }
    set children(children) {
        this._children = children;
    }
    set formulaDef(value) {
        this._formulaDef = value;
    }
    getArgIndex(arg) {
        return this.children.findIndex(child => child === arg);
    }
    toString() {
        const argsString = this._children.map(child => child.toString()).join('');
        return `${this._formulaName}${argsString}`;
    }
    translate(opts) {
        this.translateFormulaName(opts);
        const argsString = super.translate(opts);
        //TODO: traduzir enums, dates, etc
        return `${this._translatedFormulaName}${argsString}`;
    }
    translateFormulaName(opts) {
        const formulaName = this._formulaName;
        const translatedFormulaName = opts.translations?.formulas[formulaName]?.alias.toUpperCase() ?? formulaName;
        // locale -> en (setting canonical formula name)
        this._abstractText = translatedFormulaName;
        // en -> locale (setting canonical formula name)
        if (opts.isOut)
            this._abstractText = formulaName;
        this._translatedFormulaName = translatedFormulaName;
        const formulaDef = sheet_db_1.FormulaLibrary[this._abstractText];
        this._formulaDef = formulaDef;
        const formulaTranslations = opts.translations?.formulas[formulaName];
        this._formulaTranslations = formulaTranslations;
        this.createFormulaData(formulaDef, formulaTranslations);
    }
    createFormulaData(formulaDef, formulaTranslation) {
        if (!formulaDef)
            return;
        const argNames = formulaTranslation?.arg_names ?? formulaDef.arg_names;
        const argDescriptions = formulaTranslation?.args_description ?? formulaDef.args_description;
        const formattedArgs = argNames.map((argName, argIndex) => {
            const argDef = formulaDef.args_def[argIndex];
            const argDescription = argDescriptions[argIndex];
            const required = argDef.required;
            const variadic = argDef.variadic ?? false;
            const replaceNormalizer = argDef.replaceNormalizer;
            const formattedArgTypes = argDef.types.map(type => {
                if (!type.startsWith('enum'))
                    return type;
                // doesnt have replacements to this arg type
                if (!formulaTranslation?.replacements?.[argIndex])
                    return type;
                let translatedType = type;
                // has translation
                Object.entries(formulaTranslation?.replacements?.[argIndex] ?? {}).map(([localeEnum, canonicalEnum]) => {
                    translatedType = translatedType.replace(canonicalEnum, localeEnum);
                });
                return translatedType;
            });
            const argData = {
                name: argName,
                description: argDescription,
                types: formattedArgTypes,
                required,
                variadic,
                replaceNormalizer,
            };
            return argData;
        });
        this._formulaData = {
            name: this._formulaName,
            description: formulaTranslation?.description ?? formulaDef.description,
            returnType: formulaDef.return_type,
            // if the formula has a window function type, it is a window function
            isWindowFunction: !!formulaDef.special_fn_type,
            args: formattedArgs,
        };
    }
    isMultipleReturnType() {
        if (!this.formulaDef)
            return false;
        return !!this.formulaDef?.multipleReturnType;
    }
    getReturnType() {
        if (!this.formulaDef)
            return undefined;
        return this.formulaDef.return_type;
    }
}
exports.FormulaNode = FormulaNode;
